import { useCallback, useEffect, useRef, useState } from 'react';

export type TutorialDemonstracaoMediaDevice = {
	deviceId: string;
	label: string;
};

export type TutorialDemonstracaoMediaState = {
	stream: MediaStream | null;
	screenStream: MediaStream | null;
	isScreenSharing: boolean;
	camOn: boolean;
	micOn: boolean;
	cameras: TutorialDemonstracaoMediaDevice[];
	microphones: TutorialDemonstracaoMediaDevice[];
	speakers: TutorialDemonstracaoMediaDevice[];
	selectedCameraId: string;
	selectedMicrophoneId: string;
	selectedSpeakerId: string;
	isLoading: boolean;
	screenShareError: string | null;
	error: string | null;
	toggleCamera: () => void;
	toggleMicrophone: () => void;
	startScreenShare: () => Promise<void>;
	stopScreenShare: () => void;
	selectCamera: (deviceId: string) => void;
	selectMicrophone: (deviceId: string) => void;
	selectSpeaker: (deviceId: string) => void;
};

const mapDevices = (
	devices: MediaDeviceInfo[],
	kind: MediaDeviceInfo['kind'],
): TutorialDemonstracaoMediaDevice[] =>
	devices
		.filter((device) => device.kind === kind)
		.map((device, index) => ({
			deviceId: device.deviceId,
			label: device.label || `Dispositivo ${index + 1}`,
		}));

export function useTutorialDemonstracaoMedia(
	enabled: boolean,
): TutorialDemonstracaoMediaState {
	const streamRef = useRef<MediaStream | null>(null);
	const screenStreamRef = useRef<MediaStream | null>(null);
	const [stream, setStream] = useState<MediaStream | null>(null);
	const [screenStream, setScreenStream] = useState<MediaStream | null>(null);
	const [isScreenSharing, setIsScreenSharing] = useState(false);
	const [camOn, setCamOn] = useState(true);
	const [micOn, setMicOn] = useState(true);
	const [cameras, setCameras] = useState<TutorialDemonstracaoMediaDevice[]>(
		[],
	);
	const [microphones, setMicrophones] = useState<
		TutorialDemonstracaoMediaDevice[]
	>([]);
	const [speakers, setSpeakers] = useState<TutorialDemonstracaoMediaDevice[]>(
		[],
	);
	const [selectedCameraId, setSelectedCameraId] = useState('');
	const [selectedMicrophoneId, setSelectedMicrophoneId] = useState('');
	const [selectedSpeakerId, setSelectedSpeakerId] = useState('');
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [screenShareError, setScreenShareError] = useState<string | null>(
		null,
	);

	const stopScreenShare = useCallback(() => {
		screenStreamRef.current?.getTracks().forEach((track) => track.stop());
		screenStreamRef.current = null;
		setScreenStream(null);
		setIsScreenSharing(false);
	}, []);

	const stopStream = useCallback(() => {
		streamRef.current?.getTracks().forEach((track) => track.stop());
		streamRef.current = null;
		setStream(null);
	}, []);

	const refreshDeviceLists = useCallback(async () => {
		if (!navigator.mediaDevices?.enumerateDevices) return;

		const devices = await navigator.mediaDevices.enumerateDevices();
		const nextCameras = mapDevices(devices, 'videoinput');
		const nextMicrophones = mapDevices(devices, 'audioinput');
		const nextSpeakers = mapDevices(devices, 'audiooutput');

		setCameras(nextCameras);
		setMicrophones(nextMicrophones);
		setSpeakers(nextSpeakers);

		setSelectedCameraId((current) => {
			if (
				current &&
				nextCameras.some((device) => device.deviceId === current)
			) {
				return current;
			}
			return nextCameras[0]?.deviceId ?? '';
		});
		setSelectedMicrophoneId((current) => {
			if (
				current &&
				nextMicrophones.some((device) => device.deviceId === current)
			) {
				return current;
			}
			return nextMicrophones[0]?.deviceId ?? '';
		});
		setSelectedSpeakerId((current) => {
			if (
				current &&
				nextSpeakers.some((device) => device.deviceId === current)
			) {
				return current;
			}
			return nextSpeakers[0]?.deviceId ?? '';
		});
	}, []);

	useEffect(() => {
		if (!enabled) {
			stopScreenShare();
			stopStream();
			return;
		}

		if (!navigator.mediaDevices?.getUserMedia) {
			setError('Seu navegador não suporta acesso à câmera.');
			return;
		}

		let cancelled = false;

		const acquireStream = async () => {
			setIsLoading(true);
			setError(null);

			try {
				await refreshDeviceLists();
				stopStream();

				const constraints: MediaStreamConstraints = {
					video: selectedCameraId
						? { deviceId: { exact: selectedCameraId } }
						: true,
					audio: selectedMicrophoneId
						? { deviceId: { exact: selectedMicrophoneId } }
						: true,
				};

				const nextStream =
					await navigator.mediaDevices.getUserMedia(constraints);
				if (cancelled) {
					nextStream.getTracks().forEach((track) => track.stop());
					return;
				}

				streamRef.current = nextStream;
				setStream(nextStream);
				await refreshDeviceLists();
			} catch {
				if (!cancelled) {
					await refreshDeviceLists();
					setError(
						'Não foi possível acessar câmera ou microfone. Verifique as permissões.',
					);
				}
			} finally {
				if (!cancelled) setIsLoading(false);
			}
		};

		void acquireStream();

		return () => {
			cancelled = true;
			stopStream();
		};
	}, [
		enabled,
		refreshDeviceLists,
		selectedCameraId,
		selectedMicrophoneId,
		stopScreenShare,
		stopStream,
	]);

	useEffect(() => {
		const current = streamRef.current;
		if (!current) return;

		current.getVideoTracks().forEach((track) => {
			track.enabled = camOn;
		});
		current.getAudioTracks().forEach((track) => {
			track.enabled = micOn;
		});
	}, [camOn, micOn, stream]);

	const toggleCamera = useCallback(() => {
		setCamOn((current) => !current);
	}, []);

	const toggleMicrophone = useCallback(() => {
		setMicOn((current) => !current);
	}, []);

	const selectCamera = useCallback((deviceId: string) => {
		setSelectedCameraId(deviceId);
	}, []);

	const selectMicrophone = useCallback((deviceId: string) => {
		setSelectedMicrophoneId(deviceId);
	}, []);

	const selectSpeaker = useCallback((deviceId: string) => {
		setSelectedSpeakerId(deviceId);
	}, []);

	const startScreenShare = useCallback(async () => {
		if (!navigator.mediaDevices?.getDisplayMedia) {
			setScreenShareError(
				'Seu navegador não suporta compartilhamento de tela.',
			);
			return;
		}

		setScreenShareError(null);

		try {
			stopScreenShare();

			const nextScreenStream =
				await navigator.mediaDevices.getDisplayMedia({
					video: true,
					audio: false,
				});

			const videoTrack = nextScreenStream.getVideoTracks()[0];
			if (videoTrack) {
				videoTrack.addEventListener('ended', () => {
					stopScreenShare();
				});
			}

			screenStreamRef.current = nextScreenStream;
			setScreenStream(nextScreenStream);
			setIsScreenSharing(true);
		} catch {
			setScreenShareError(null);
		}
	}, [stopScreenShare]);

	return {
		stream,
		screenStream,
		isScreenSharing,
		camOn,
		micOn,
		cameras,
		microphones,
		speakers,
		selectedCameraId,
		selectedMicrophoneId,
		selectedSpeakerId,
		isLoading,
		screenShareError,
		error,
		toggleCamera,
		toggleMicrophone,
		startScreenShare,
		stopScreenShare,
		selectCamera,
		selectMicrophone,
		selectSpeaker,
	};
}
